<script lang="ts">
  import Form from "../../components/Form.svelte";
  import RadioGroup from "../../components/RadioGroup.svelte";
  import SharepointFileInput from "../../components/SharepointFileInput.svelte";
  import Caret from "~icons/ph/caret-down";
  import CalendarCheck from "~icons/ph/calendar-check";
  import { domId } from "../../lib/dom";

  let {
    action,
    errors,
    clientFirstName,
    deathCertificateExampleUrl,
    createUploadSessionUrl,
    uploadAttachmentExtensions,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    clientFirstName: string;
    deathCertificateExampleUrl: string;
    createUploadSessionUrl: string;
    uploadAttachmentExtensions: string[];
  } = $props();

  let selectedOption = $state("yes");

  const closeDialogId = domId("close-dialog");
</script>

<Form method="patch" {action} class="ui-rhythm">
  <h2 class="ui-title">Death certificate</h2>

  <p>
    Please accept our condolences—we’re here to help you navigate {clientFirstName}'s claim and ensure the transition
    is handled with care.
  </p>
  <p>To move forward, we need to confirm their passing. To do that, please provide their death certificate.</p>

  <details class="ui-details">
    <summary>
      <Caret />What is a death certificate and how do I get one?
    </summary>

    <p>
      A death certificate is an official, certified document issued by a government registrar that provides a legal
      record of a person's death. You must register a death within 5 days in England, Wales, and Northern Ireland to
      receive it, and can do so online at the
      <a href="https://www.gov.uk/register-a-death" target="_blank" rel="noreferrer" class="link text-blue-600">
        official government site
      </a>. They generally cost £12.50 per copy from the register office, or can be ordered online and received
      within 4-7 business days.
    </p>

    <p>Example death certificate:</p>
    <img src={deathCertificateExampleUrl} alt="Example of a certified copy of a death registration entry" class="rounded-lg border mt-2" />
  </details>

  <fieldset class="ui-fieldset">
    <legend>Do you have the death certificate?</legend>

    <RadioGroup
      name="has_document"
      options={[
        ["Yes", "yes"],
        ["No, I need time to get it", "no"],
      ]}
      bind:group={selectedOption}
      required />
  </fieldset>

  <div class="ui-content-options mt-6">
    <fieldset class="ui-fieldset" class:invisible={selectedOption !== "yes"}>
      <legend>Upload death certificate</legend>

      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}

      <div>
        <p class="ui-alert ui-alert-info pb-4 -mb-2.5 mt-0">Please provide clear and legible photo(s) of the death certificate. Add all pages before sending.</p>
        <SharepointFileInput
          name="death_certificate"
          accept={uploadAttachmentExtensions}
          {createUploadSessionUrl}
          multiple
          required={selectedOption === "yes"} />
      </div>

      {#if errors?.death_certificate}<div class="ui-error">{errors.death_certificate}</div>{/if}
    </fieldset>

    <div class:invisible={selectedOption !== "no"}>
      <div class="ui-alert ui-alert-info">
        <CalendarCheck />
        <p>
          Take all the time you need, and we’ll check back in a month. Until then, feel free to use our resources above
          to find this document. When you find it, you can upload it here.
        </p>
      </div>

      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}
    </div>
  </div>

  <div class="floating-footer">
    <button class="btn btn-ghost" form={closeDialogId}>Cancel</button>
    <button class="btn btn-primary" type="submit" data-viewer-mode>{selectedOption === "yes" ? "Send" : "Finish"}</button>
  </div>
</Form>

<form method="dialog" id={closeDialogId}></form>
